import { connect } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import { Menu } from "primereact/menu";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import { useIntl } from "react-intl";
import List from "../../../common/list";
import { TextInput } from "../../../common/fields";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import {
  listRequest,
  setQuery,
  setFilters,
  performActionRequest,
  summaryRequest,
  downloadTemplateRequest,
  uploadUsersRequest,
  userExportRequest,
} from "../../../../store/slices/usersSlice";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { SplitButton } from "primereact/splitbutton";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import Form from "../../../common/form";
import ImportUsersForm from "./ImportUsersForm";
import UserFilters from "../filters";
import DropdownButton from "../../../common/dropDownButton";
import useMobileView from "../../../common/hooks/useMobileView";

const UserList = ({
  action,
  downloadTemplateRequest,
  error,
  filters,
  labels,
  list,
  listRequest,
  performActionRequest,
  query,
  requesting,
  sections,
  setFilters,
  setQuery,
  summary,
  summaryRequest,
  uploadUsersRequest,
  user,
  totalCount = 0,
  userExportRequest,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const menuRef = useRef();
  const { isMobile } = useMobileView();

  const [namePattern, setNamePattern] = useState("");
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [isSummaryShown, setIsSummaryShown] = useState(false);
  const [isMobileViewSummaryShown, setIsmobileViewSummaryShown] =
    useState(false);
  const [isImportDialogVisible, setIsImportDialogVisible] = useState(false);
  const [importFile, setImportFile] = useState(null);

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.CreateScope
  );

  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.UpdateScope
  );

  const canDelete = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.DeleteScope
  );

  const fetchList = useCallback(() => {
    listRequest({
      query: {
        ...query,
        sortColumn:
          query.sortColumn === "name" ? "firstName" : query.sortColumn,
      },
      filters: filters
        .filter((item) => item.values.length > 0)
        .map((item) => ({
          id: item.id,
          values: item.values,
          isDynamicField:
            item.id !== "Designation" &&
            item.id !== "Department" &&
            item.id !== "ManagerId",
        })),
    });
    summaryRequest();
  }, [query, listRequest, summaryRequest, filters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (
      !requesting &&
      !error &&
      (action === "delete" ||
        action === "activate" ||
        action === "deactivate" ||
        action === "upload-users")
    ) {
      if (action === "delete") {
        setQuery({ pageNo: 1 });
      } else {
        fetchList();
      }
    }
  }, [action, requesting, error, setQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query?.namePattern !== namePattern) {
        setQuery({ namePattern, pageNo: 1 });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [namePattern, setQuery, query]);

  useEffect(() => {
    if (!isMobile && isMobileViewSummaryShown) {
      setIsmobileViewSummaryShown(false);
    }

    if (isMobile && isSummaryShown) {
      setIsSummaryShown(false);
    }
  }, [isMobile]);

  const onSelectionChange = (selectedRecords) => {
    setSelectedRecords(selectedRecords?.value || []);
  };

  const handlePageChange = (pageData) => {
    if (
      query.pageSize !== pageData.rows ||
      query.pageNo !== pageData.page + 1
    ) {
      setQuery({
        ...query,
        pageNo: pageData.page + 1,
        pageSize: pageData.rows,
      });
    }
  };

  const handleOnSortChange = (sortData) => {
    setQuery({
      ...query,
      pageNo: 1,
      sortColumn: sortData.sortField,
      sortBy: sortData.sortOrder === 1 ? "asc" : "desc",
    });
  };

  const handleAddUser = () => {
    navigate(RouteConstants.APP_ROUTE_ADMINISTRATION_USERS_ADD);
  };

  const onDeleteAction = (data) => {
    confirmDialog({
      message: intl.formatMessage(
        { id: "delete_object_message" },
        {
          name: data.email,
          object: intl.formatMessage({ id: "user" }),
        }
      ),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => {
        performActionRequest({ action: "delete", id: data.id });
      },
      reject: () => {},
    });
  };

  const handleImportUsers = () => {
    uploadUsersRequest({ file: importFile });
    setIsImportDialogVisible(false);
    setImportFile(null);
  };

  const customFields = sections.reduce((acc, section) => {
    acc = [
      ...acc,
      ...(section?.fields?.map((item) => ({
        id: item.id,
        name: item.name,
        value: item.id,
        type: item.type,
        label: item.label,
      })) || []),
    ];
    return acc;
  }, []);

  const exportUsers = (fileType) => {
    userExportRequest({
      fileType,
      query: {
        ...query,
        sortColumn:
          query.sortColumn === "name" ? "firstName" : query.sortColumn,
      },
      filters: filters
        .filter((item) => item.values.length > 0)
        .map((item) => ({
          id: item.id,
          values: item.values,
          isDynamicField:
            item.id !== "Designation" &&
            item.id !== "Department" &&
            item.id !== "ManagerId",
        })),
    });
  };

  return (
    <div className="pb-8 md:pb-0">
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "users" })}
            <div className="edexlms-page-summary-options hidden md:block">
              <Button link onClick={() => setIsSummaryShown(!isSummaryShown)}>
                {intl.formatMessage({
                  id: !isSummaryShown ? "show_summary" : "hide_summary",
                })}
              </Button>
            </div>
          </div>
          <div className="edexlms-page-actions flex gap-2">
            <SplitButton
              icon="pi pi-plus"
              onClick={handleAddUser}
              size="small"
              label={intl.formatMessage({ id: "add_user" })}
              type="button"
              disabled={!canAdd}
              tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
              tooltip={
                canAdd ? "" : intl.formatMessage({ id: "no_permission" })
              }
              model={[
                {
                  label: intl.formatMessage({ id: "download_template" }),
                  icon: "pi pi-cloud-download",
                  command: () => {
                    downloadTemplateRequest();
                  },
                },
                {
                  label: intl.formatMessage({ id: "bulk_import" }),
                  icon: "pi pi-cloud-upload",
                  command: () => {
                    setIsImportDialogVisible(true);
                  },
                },
              ]}
            />

            <div className="block md:hidden">
              <Button
                icon="pi pi-ellipsis-v"
                style={{
                  color: "var(--edexlms-primary-color)",
                  padding: 0,
                  width: "100%",
                  height: "30px",
                  fontSize: "2rem",
                  backgroundColor: "ButtonFace",
                }}
                onClick={(event) => menuRef.current.toggle(event)}
              ></Button>
              <Menu
                ref={menuRef}
                model={[
                  {
                    label: "Export",
                    icon: "pi pi-fw pi-plus",
                    disabled: true,
                    template: () => {
                      return (
                        <div>
                          <div className="flex align-items-center px-2 py-2 gap-2">
                            <i
                              className="pi pi-download"
                              style={{ fontSize: "1rem", fontWeight: "bold" }}
                            ></i>
                            <span
                              style={{
                                fontSize: "1.1rem",
                                fontWeight: "bold",
                              }}
                            >
                              Export
                            </span>
                          </div>
                          <ul className=" export-menu-container">
                            <li onClick={() => exportUsers("excel")}>
                              - EXCEL{" "}
                            </li>
                            <li onClick={() => exportUsers("csv")}>- CSV </li>
                            <li onClick={() => exportUsers("pdf")}>- PDF</li>
                          </ul>
                        </div>
                      );
                    },
                  },
                  {
                    label: "Show staticstics",
                    icon: "pi pi-fw pi-plus",
                    disabled: true,
                    template: () => {
                      return (
                        <div
                          className="flex align-items-center px-2 py-2 gap-2 cursor-pointer"
                          style={{ fontWeight: "bold" }}
                          onClick={() => setIsmobileViewSummaryShown(true)}
                        >
                          <i className="pi pi-chart-bar" />
                          Show staticstics
                        </div>
                      );
                    },
                  },
                ]}
                popup
                id="popup_menu_right"
                popupAlignment="right"
              />
            </div>

            <div className="hidden md:block">
              <DropdownButton
                icon="pi pi-download"
                label={intl.formatMessage({ id: "export" })}
                items={[
                  {
                    label: intl.formatMessage({ id: "excel" }),
                    command: () => exportUsers("excel"),
                  },
                  {
                    label: intl.formatMessage({ id: "csv" }),
                    command: () => exportUsers("csv"),
                  },
                  {
                    label: intl.formatMessage({ id: "pdf" }),
                    command: () => exportUsers("pdf"),
                  },
                ]}
              />
            </div>
          </div>
        </div>
        {(isSummaryShown && (
          <div className="edexlms-page-summary">
            <div className="edexlms-summary-total">
              <div className="edexlms-summary-type">
                <i className="pi pi-users text-2xl"></i>
              </div>
              <div className="edexlms-summary-count-summary flex flex-column">
                <span className="edexlms-summary-title">
                  {intl.formatMessage({ id: "total" })}
                </span>
                <span className="edexlms-summary-count">{summary.total}</span>
              </div>
            </div>
            <div className="edexlms-summary-total">
              <div className="edexlms-summary-count-summary flex align-items-center gap-2">
                <div
                  className="edexlms-summary-color"
                  style={{ backgroundColor: "#1DA750" }}
                />
                <div className="flex flex-column">
                  <span className="edexlms-summary-title">
                    {intl.formatMessage({ id: "active" })}
                  </span>
                  <span className="edexlms-summary-count">
                    {summary?.userStatusSummary?.activeCount || 0}
                  </span>
                </div>
              </div>
              <div className="edexlms-summary-count-summary flex align-items-center gap-2">
                <div
                  className="edexlms-summary-color"
                  style={{ backgroundColor: "#FF3D32" }}
                />
                <div className="flex flex-column">
                  <span className="edexlms-summary-title">
                    {intl.formatMessage({ id: "inactive" })}
                  </span>
                  <span className="edexlms-summary-count">
                    {summary?.userStatusSummary?.inactiveCount || 0}
                  </span>
                </div>
              </div>
            </div>
            <div className="edexlms-summary-total">
              <div className="edexlms-summary-count-summary flex align-items-center gap-2">
                <div
                  className="edexlms-summary-color"
                  style={{ backgroundColor: "#1DA750" }}
                />
                <div className="flex flex-column">
                  <span className="edexlms-summary-title">
                    {intl.formatMessage({ id: "compliant" })}
                  </span>
                  <span className="edexlms-summary-count">
                    {summary?.userComplianceSummary?.complianceCount || 0}
                  </span>
                </div>
              </div>
              <div className="edexlms-summary-count-summary flex align-items-center gap-2">
                <div
                  className="edexlms-summary-color"
                  style={{ backgroundColor: "#D9342B" }}
                />
                <div className="flex flex-column">
                  <span className="edexlms-summary-title">
                    {intl.formatMessage({ id: "non_compliant" })}
                  </span>
                  <span className="edexlms-summary-count">
                    {summary?.userComplianceSummary?.nonComplianceCount || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )) ||
          null}
        <div className="edexlms-page-body">
          <div className={`edexlms-search-filter-bar`}>
            <TextInput
              iconPosition="left"
              className={`${isMobile ? "mobile" : ""}`}
              icon="pi-search"
              placeholder={intl.formatMessage({ id: "search_users" })}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
            <UserFilters filters={filters} onFilterChange={setFilters} />
          </div>
          <List
            loading={action === "list" && requesting}
            onSelectionChange={onSelectionChange}
            handlePageChange={handlePageChange}
            handleOnSortChange={handleOnSortChange}
            data={list || []}
            selectedRecords={selectedRecords}
            pageQuery={query}
            totalRecords={totalCount}
            handleRowClick={(data) => {
              navigate(
                `${RouteConstants.APP_ROUTE_ADMINISTRATION_USERS_EDIT.replace(
                  ":id",
                  data.data.id
                )}`
              );
            }}
            columns={[
              {
                field: "firstName",
                header: "name",
                sortable: true,
                body: ListCellRenders.FullNameAndEmail(),
              },
              { field: "role.name", header: "role" },
              {
                field: "designation",
                header: "designation",
                sortable: true,
                body: ListCellRenders.StringData("designation"),
              },
              {
                field: "department",
                header: "department",
                sortable: true,
                body: ListCellRenders.StringData("department"),
              },
              {
                field: "isActive",
                header: "status",
                sortable: true,
                body: ListCellRenders.Status,
              },
              ...customFields
                .filter((item) => item.type !== 5)
                .filter(
                  (item) =>
                    filters.filter((filter) => filter.id === item.id)?.length >
                    0
                )
                .map((item) => ({
                  field: item.label,
                  header: item.label,
                  sortable: false,
                  i18n: false,
                  body: (rowData) => {
                    return (
                      rowData?.dynamicValues?.find(
                        (field) => field.customFieldId === item.id
                      )?.value || "-"
                    );
                  },
                })),
              {
                field: "actions",
                headerIcon: "pi-cog",
                align: "center",
                body: ListCellRenders.OtherActions(
                  [
                    {
                      label: "delete",
                      icon: "pi-trash",
                      isAllowed: canDelete,
                      action: (data) => onDeleteAction(data),
                    },
                    {
                      label: "activate",
                      icon: "pi-check",
                      isAllowed: canUpdate,
                      action: (data) =>
                        performActionRequest({
                          action: "activate",
                          id: data.id,
                        }),
                      hidden: {
                        field: "isActive",
                        value: true,
                      },
                    },
                    {
                      label: "deactivate",
                      icon: "pi-times",
                      isAllowed: canUpdate,
                      action: (data) =>
                        performActionRequest({
                          action: "deactivate",
                          id: data.id,
                        }),
                      hidden: {
                        field: "isActive",
                        value: false,
                      },
                    },
                  ],
                  "id",
                  user?.id
                ),
              },
            ]}
          />
        </div>
      </div>
      <div className="edexlms-page-footer">
        {(selectedRecords.length > 0 && (
          <div className="edexlms-page-footer-actions">
            <div className="edexlms-selected-count">
              <span>
                {intl.formatMessage(
                  { id: "selected_users" },
                  { count: selectedRecords.length }
                )}
              </span>
              <Button
                style={{ color: "white" }}
                text
                onClick={() => setSelectedRecords([])}
              >
                {intl.formatMessage({ id: "clear_selection" })}
              </Button>
            </div>
            <div className="edexlms-selected-actions flex gap-2">
              {(canUpdate && (
                <>
                  <Button
                    icon="pi pi-check"
                    outlined
                    label={intl.formatMessage({ id: "activate" })}
                    type="button"
                    size="small"
                    onClick={() => {
                      performActionRequest({
                        action: "activate",
                        ids: selectedRecords.map((item) => item.id),
                      });
                      setSelectedRecords([]);
                    }}
                    style={{ backgroundColor: "white" }}
                  />
                  <Button
                    icon="pi pi-times"
                    outlined
                    label={intl.formatMessage({ id: "deactivate" })}
                    type="button"
                    size="small"
                    onClick={() => {
                      performActionRequest({
                        action: "deactivate",
                        ids: selectedRecords.map((item) => item.id),
                      });
                      setSelectedRecords([]);
                    }}
                    style={{ backgroundColor: "white" }}
                  />
                </>
              )) ||
                null}
            </div>
          </div>
        )) ||
          null}
      </div>
      <ConfirmDialog />
      <Dialog
        style={{ width: "50vw", minWidth: "400px" }}
        visible={isImportDialogVisible}
        onHide={() => {
          setIsImportDialogVisible(false);
          setImportFile(null);
        }}
        header={intl.formatMessage({ id: "bulk_import_users" })}
      >
        <Form
          submitBtn={{
            label: intl.formatMessage({ id: "bulk_import_users" }),
            onClick: (values) => {
              handleImportUsers(values);
            },
          }}
          disabled={!importFile}
          cancelBtn={{
            label: intl.formatMessage({ id: "cancel" }),
            onClick: () => {
              setIsImportDialogVisible(false);
              setImportFile(null);
            },
          }}
        >
          <ImportUsersForm
            onClear={() => setImportFile(null)}
            onFileSelect={(event) => setImportFile(event.files[0])}
          />
        </Form>
      </Dialog>
      {isMobileViewSummaryShown && (
        <Dialog
          visible={isMobileViewSummaryShown}
          draggable={false}
          onHide={(e) => {
            setIsmobileViewSummaryShown(false);
          }}
          style={{ width: "50vw", minWidth: "400px" }}
          header={() => {
            return <div>Statistics</div>;
          }}
        >
          <div
            className="flex flex-column"
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 6,
            }}
          >
            <div className="edexlms-page-summary w-full border-none m-0">
              <div className="edexlms-summary-total">
                <div className="edexlms-summary-type">
                  <i className="pi pi-users text-2xl"></i>
                </div>
                <div className="edexlms-summary-count-summary flex flex-column">
                  <span className="edexlms-summary-title">
                    {intl.formatMessage({ id: "total" })}
                  </span>
                  <span className="edexlms-summary-count">{summary.total}</span>
                </div>
              </div>
            </div>
            <div className=" edexlms-page-summary flex w-full m-0 border-none">
              <div className="edexlms-summary-total">
                <div className="edexlms-summary-count-summary flex align-items-center gap-2">
                  <div
                    className="edexlms-summary-color"
                    style={{ backgroundColor: "#1DA750" }}
                  />
                  <div className="flex flex-column">
                    <span className="edexlms-summary-title">
                      {intl.formatMessage({ id: "active" })}
                    </span>
                    <span className="edexlms-summary-count">
                      {summary?.userStatusSummary?.activeCount || 0}
                    </span>
                  </div>
                </div>
                <div className="edexlms-summary-count-summary flex align-items-center gap-2">
                  <div
                    className="edexlms-summary-color"
                    style={{ backgroundColor: "#FF3D32" }}
                  />
                  <div className="flex flex-column">
                    <span className="edexlms-summary-title">
                      {intl.formatMessage({ id: "inactive" })}
                    </span>
                    <span className="edexlms-summary-count">
                      {summary?.userStatusSummary?.inactiveCount || 0}
                    </span>
                  </div>
                </div>
              </div>
              <div className="edexlms-summary-total">
                <div className="edexlms-summary-count-summary flex align-items-center gap-2">
                  <div
                    className="edexlms-summary-color"
                    style={{ backgroundColor: "#1DA750" }}
                  />
                  <div className="flex flex-column">
                    <span className="edexlms-summary-title">
                      {intl.formatMessage({ id: "compliant" })}
                    </span>
                    <span className="edexlms-summary-count">
                      {summary?.userComplianceSummary?.complianceCount || 0}
                    </span>
                  </div>
                </div>
                <div className="edexlms-summary-count-summary flex align-items-center gap-2">
                  <div
                    className="edexlms-summary-color"
                    style={{ backgroundColor: "#D9342B" }}
                  />
                  <div className="flex flex-column">
                    <span className="edexlms-summary-title">
                      {intl.formatMessage({ id: "non_compliant" })}
                    </span>
                    <span className="edexlms-summary-count">
                      {summary?.userComplianceSummary?.nonComplianceCount || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  action: state?.users?.action,
  error: state?.users?.error,
  filters: state?.users?.filters,
  list: state?.users?.data?.list || [],
  totalCount: state?.users?.data?.totalCount,
  query: state?.users?.query,
  requesting: state?.users?.requesting,
  sections: state.customFields.data,
  summary: state?.users?.summary,
  user: state?.self?.user,
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: (data) => dispatch(listRequest(data)),
  setQuery: (data) => dispatch(setQuery(data)),
  performActionRequest: (data) => dispatch(performActionRequest(data)),
  summaryRequest: (data) => dispatch(summaryRequest(data)),
  downloadTemplateRequest: () => dispatch(downloadTemplateRequest()),
  uploadUsersRequest: (data) => dispatch(uploadUsersRequest(data)),
  setFilters: (data) => dispatch(setFilters(data)),
  userExportRequest: (data) => dispatch(userExportRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserList);
